import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config/api'; // Ensure BASE_URL is correctly set in your config

const EditPurchaseRequisition = () => {
    const { id } = useParams(); // Get the purchase requisition ID from the URL
    const navigate = useNavigate();

    // State for holding the requisition data
    const [formData, setFormData] = useState({
        packUnit: '',
        packQty: '',
        unit: '',
        quantity: '',
        rate: '',
        amount: '',
        mrp: '',
        netRate: '',
        remark: '',
        itemBarcode: '',
        itemBalance: '',
        lineLevelBarcode: '',
        hsnCode: '',
        brand: ''
    });

    // State for loading, success, and error handling
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch requisition details when the component mounts
    useEffect(() => {
        setLoading(true);  // Set loading to true when starting the fetch
        setError(null);  // Clear previous errors

        console.log(`Fetching requisition data for ID: ${id}`); // Debugging log

        fetch(`${BASE_URL}purchaseRequisition/${id}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }
                return response.json();
            })
            .then(data => {
                console.log('Fetched Data:', data); // Debugging log
                if (data && data.id) {
                    // Populate form fields with the fetched data
                    setFormData({
                        packUnit: data.pack_unit || '',
                        packQty: data.pack_qty || '',
                        unit: data.unit || '',
                        quantity: data.quantity || '',
                        rate: data.rate || '',
                        amount: data.amount || '',
                        mrp: data.mrp || '',
                        netRate: data.net_rate || '',
                        remark: data.remark || '',
                        itemBarcode: data.item_barcode || '',
                        itemBalance: data.item_balance || '',
                        lineLevelBarcode: data.line_level_barcode || '',
                        hsnCode: data.hsn_code || '',
                        brand: data.brand || ''
                    });
                    setLoading(false); // Set loading to false once data is fetched
                } else {
                    throw new Error('Requisition not found or invalid data');
                }
            })
            .catch(error => {
                console.error('Error fetching requisition:', error); // Debugging log
                setError(error.message || 'Error fetching requisition details');
                setLoading(false);
            });
    }, [id]);

    // Handle form field changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();

        const updatedRequisition = {
            pack_unit: formData.packUnit,
            pack_qty: formData.packQty,
            unit: formData.unit,
            quantity: formData.quantity,
            rate: formData.rate,
            amount: formData.amount,
            mrp: formData.mrp,
            net_rate: formData.netRate,
            remark: formData.remark,
            item_barcode: formData.itemBarcode,
            item_balance: formData.itemBalance,
            line_level_barcode: formData.lineLevelBarcode,
            hsn_code: formData.hsnCode,
            brand: formData.brand,
        };

        setLoading(true); // Set loading to true while submitting

        fetch(`${BASE_URL}purchaseRequisition/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedRequisition),
        })
            .then(response => response.json())
            .then(data => {
                setLoading(false); // Set loading to false after response
                if (data.status === 'success') {
                    alert('Requisition updated successfully!');
                    navigate('/PurchaseRequisitionList'); // Redirect to list after success
                } else {
                    alert('Error updating requisition. Please try again.');
                }
            })
            .catch(error => {
                setLoading(false); // Set loading to false after error
                setError('Error updating requisition');
                console.error('Error updating requisition:', error);
            });
    };

    // Render Loading, Error, or Form based on states
    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Edit Purchase Requisition</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb">
                                    {/* You can add breadcrumb links here */}
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Purchase Requisition Form */}
            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                {/* Optional header content */}
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="packUnit" className="form-label">Pack Unit</label>
                                        <input
                                            type="text"
                                            id="packUnit"
                                            name="packUnit"
                                            className="form-control"
                                            value={formData.packUnit}
                                            onChange={handleInputChange}
                                            placeholder="Enter pack unit"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="packQty" className="form-label">Pack Quantity</label>
                                        <input
                                            type="number"
                                            id="packQty"
                                            name="packQty"
                                            className="form-control"
                                            value={formData.packQty}
                                            onChange={handleInputChange}
                                            placeholder="Enter pack quantity"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="unit" className="form-label">Unit</label>
                                        <input
                                            type="text"
                                            id="unit"
                                            name="unit"
                                            className="form-control"
                                            value={formData.unit}
                                            onChange={handleInputChange}
                                            placeholder="Enter unit"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="quantity" className="form-label">Quantity</label>
                                        <input
                                            type="number"
                                            id="quantity"
                                            name="quantity"
                                            className="form-control"
                                            value={formData.quantity}
                                            onChange={handleInputChange}
                                            placeholder="Enter quantity"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="rate" className="form-label">Rate</label>
                                        <input
                                            type="number"
                                            id="rate"
                                            name="rate"
                                            className="form-control"
                                            value={formData.rate}
                                            onChange={handleInputChange}
                                            placeholder="Enter rate"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="amount" className="form-label">Amount</label>
                                        <input
                                            type="number"
                                            id="amount"
                                            name="amount"
                                            className="form-control"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            placeholder="Enter amount"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="mrp" className="form-label">MRP</label>
                                        <input
                                            type="number"
                                            id="mrp"
                                            name="mrp"
                                            className="form-control"
                                            value={formData.mrp}
                                            onChange={handleInputChange}
                                            placeholder="Enter MRP"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="netRate" className="form-label">Net Rate</label>
                                        <input
                                            type="number"
                                            id="netRate"
                                            name="netRate"
                                            className="form-control"
                                            value={formData.netRate}
                                            onChange={handleInputChange}
                                            placeholder="Enter net rate"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="remark" className="form-label">Remark</label>
                                        <input
                                            type="text"
                                            id="remark"
                                            name="remark"
                                            className="form-control"
                                            value={formData.remark}
                                            onChange={handleInputChange}
                                            placeholder="Enter remark"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="itemBarcode" className="form-label">Item Barcode</label>
                                        <input
                                            type="text"
                                            id="itemBarcode"
                                            name="itemBarcode"
                                            className="form-control"
                                            value={formData.itemBarcode}
                                            onChange={handleInputChange}
                                            placeholder="Enter item barcode"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="itemBalance" className="form-label">Item Balance</label>
                                        <input
                                            type="number"
                                            id="itemBalance"
                                            name="itemBalance"
                                            className="form-control"
                                            value={formData.itemBalance}
                                            onChange={handleInputChange}
                                            placeholder="Enter item balance"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="lineLevelBarcode" className="form-label">Line Level Barcode</label>
                                        <input
                                            type="text"
                                            id="lineLevelBarcode"
                                            name="lineLevelBarcode"
                                            className="form-control"
                                            value={formData.lineLevelBarcode}
                                            onChange={handleInputChange}
                                            placeholder="Enter line level barcode"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="hsnCode" className="form-label">HSN Code</label>
                                        <input
                                            type="text"
                                            id="hsnCode"
                                            name="hsnCode"
                                            className="form-control"
                                            value={formData.hsnCode}
                                            onChange={handleInputChange}
                                            placeholder="Enter HSN code"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="brand" className="form-label">Brand</label>
                                        <input
                                            type="text"
                                            id="brand"
                                            name="brand"
                                            className="form-control"
                                            value={formData.brand}
                                            onChange={handleInputChange}
                                            placeholder="Enter brand"
                                            required
                                        />
                                    </div>

                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? 'Updating...' : 'Update Requisition'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EditPurchaseRequisition;
